/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { BrushStore } from 'src/luis/projects/utterances/stores/BrushStore';
import { MenuStore } from 'src/luis/projects/utterances/stores/MenuStore';
import { LuisId } from 'src/luis/types/Resource';
import { EntityRoleStore } from 'src/luis/types/stores/entities/EntityRoleStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { Selection } from 'src/modules/labeling/types/Selection';

export type LabelingMode = 'brush' | 'menu';

export type BrushAttachedData = {
    normalizedValue: string;
    entity: EntityStore;
    parentEntity?: EntityStore;
    role?: EntityRoleStore;
};

export type TokenSelectionMetadata = { selection: Selection; utteranceId: LuisId; selectedText: string } | null;

/**
 * Reactive store encapsulate all the labeling UI states.
 */
export class LabelingStore {
    public readonly brushStore: BrushStore;

    public readonly menuStore: MenuStore;

    @observable
    public tokenSelectionMetadata: TokenSelectionMetadata;

    @observable
    public mode: LabelingMode;

    constructor() {
        this.mode = 'menu';
        this.brushStore = new BrushStore();
        this.menuStore = new MenuStore();
        this.tokenSelectionMetadata = null;
    }

    /**
     * Setter for the labeling mode.
     */
    @action
    public setLabelingMode(mode: LabelingMode) {
        this.mode = mode;
    }

    /**
     * Setter for the token selection meta data object.
     */
    @action
    public setTokenSelectionMetadata(selection: TokenSelectionMetadata) {
        this.tokenSelectionMetadata = selection;
    }

    @computed
    public get disabledUtteranceId() {
        return this.mode === 'brush' && this.brushStore.invalidUtteranceId;
    }
}
